import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Container } from 'reactstrap';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Meta from 'components/App/Meta';
import Loading from 'components/App/Loading';
import ModalImagePreview from 'components/Modals/ModalImagePreview';

import {
  COMPLAINT_STATUS,
  COMPLAINT_STATUS_LIST,
  COMPLAINT_TYPE_LIST,
  LOCATIONS,
  themeColor,
} from 'constants/index';
import {
  actionGetManagerComplaintDetail,
  actionClearManagerComplaintDetail,
  actionResolveManagerComplaint,
  actionRejectManagerComplaint,
} from 'store/manager-complaint/action';

import ModalResolveComplaint from './components/ModalResolveComplaint';
import ModalRejectComplaint from './components/ModalRejectComplaint';
import styles from './ComplaintDetail.module.scss';

const getStatusBadgeColor = (status) => {
  switch (status) {
    case COMPLAINT_STATUS.RESOLVED:
      return 'success';
    case COMPLAINT_STATUS.PENDING:
      return 'caution';
    case COMPLAINT_STATUS.SKIPPED:
    case 'REJECTED':
      return 'error';
    default:
      return 'info';
  }
};

const getStatusLabel = (status) => {
  const found = COMPLAINT_STATUS_LIST.find((item) => item.value === status);
  return found ? found.label : status || '-';
};

const getTypeLabel = (type) => {
  const found = COMPLAINT_TYPE_LIST.find((item) => item.value === type);
  return found ? found.label : type || '-';
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

function ComplaintDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [preview, setPreview] = useState({
    isOpen: false,
    images: [],
    initialIndex: 0,
  });

  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  const {
    managerComplaintDetail: complaint,
    isLoadingGetManagerComplaintDetail: isLoading,
    isLoadingResolveManagerComplaint: isLoadingResolve,
    isLoadingRejectManagerComplaint: isLoadingReject,
  } = useSelector((state) => state.ManagerComplaint || {});

  useEffect(() => {
    if (id) {
      dispatch(actionGetManagerComplaintDetail(id));
    }
    return () => {
      dispatch(actionClearManagerComplaintDetail());
    };
  }, [id, dispatch]);

  const displayId = complaint?.complaintId || complaint?._id || id;
  const isPending = complaint?.status === COMPLAINT_STATUS.PENDING;

  const breadcrumbList = useMemo(() => [
    {
      name: 'Quản lý khiếu nại',
      isActive: false,
      link: LOCATIONS.MANAGER_COMPLAINT,
    },
    {
      name: 'Chi tiết khiếu nại ',
      isActive: true,
      link: '',
    },
  ], [displayId]);

  const handleConfirmResolve = (values) => {
    dispatch(
      actionResolveManagerComplaint({
        id: complaint?._id || id,
        values,
        callback: () => setIsResolveModalOpen(false),
      }),
    );
  };

  const handleConfirmReject = (values) => {
    dispatch(
      actionRejectManagerComplaint({
        id: complaint?._id || id,
        values,
        callback: () => setIsRejectModalOpen(false),
      }),
    );
  };

  return (
    <div className={`page-content ${styles.Page}`}>
      <Loading loading={isLoading} loaderOnPage />
      <Meta name={`Chi tiết khiếu nại #${displayId}`} />

      <Container fluid>
        <Breadcrumb
          pageTitle="Quản lý khiếu nại"
          breadcrumbList={breadcrumbList}
        />

        <Card className="Section">
          <div className="DetailHeader">
            <div>
              <h4 className="DetailHeader__Title">
                {`Khiếu nại: #${displayId}`}
              </h4>
            </div>

            {isPending && (
              <div className="DetailHeader__Actions">
                <Button
                  color={themeColor.ERROR}
                  onClick={() => setIsRejectModalOpen(true)}
                >
                  Từ chối
                </Button>
                <Button
                  color={themeColor.SUCCESS}
                  onClick={() => setIsResolveModalOpen(true)}
                >
                  Duyệt khiếu nại
                </Button>
              </div>
            )}
          </div>

          <div className="InfoGrid">
            <div className="InfoItem">
              <span className="InfoItem__Label">Ngày gửi khiếu nại:</span>
              <span className="InfoItem__Value">{formatDateTime(complaint?.createdAt)}</span>
            </div>

            <div className="InfoItem">
              <span className="InfoItem__Label">Khách hàng:</span>
              <span className="InfoItem__Value">
                {complaint?.user?.fullName || complaint?.customerName || '-'}
              </span>
            </div>

            <div className="InfoItem">
              <span className="InfoItem__Label">Email liên hệ:</span>
              <span className="InfoItem__Value">
                {complaint?.user?.email || complaint?.email || '-'}
              </span>
            </div>

            <div className="InfoItem">
              <span className="InfoItem__Label">Số điện thoại:</span>
              <span className="InfoItem__Value">
                {complaint?.user?.phoneNumber || complaint?.phone || '-'}
              </span>
            </div>

            <div className="InfoItem">
              <span className="InfoItem__Label">Phân loại:</span>
              <span className="InfoItem__Value">
                {getTypeLabel(complaint?.type || complaint?.category)}
              </span>
            </div>

            <div className="InfoItem">
              <span className="InfoItem__Label">Trạng thái hiện tại:</span>
              <div>
                <span
                  className={`StatusBadge StatusBadge--${getStatusBadgeColor(complaint?.status)}`}
                >
                  <span className="StatusBadgeDot" />
                  {getStatusLabel(complaint?.status)}
                </span>
              </div>
            </div>

            <div className="InfoItem">
              <span className="InfoItem__Label">Ngày giải quyết:</span>
              <span className="InfoItem__Value">
                {complaint?.responseAt || complaint?.resolvedAt
                  ? formatDateTime(complaint.responseAt || complaint.resolvedAt)
                  : 'Chưa giải quyết'}
              </span>
            </div>
          </div>

          <div className="ContentBlock">
            <h5 className="ContentBlock__Title">Nội dung khiếu nại:</h5>
            <p className="ContentBlock__Text">{complaint?.content || '-'}</p>
          </div>

          {complaint?.images && complaint.images.length > 0 && (
            <div className="ContentBlock">
              <h5 className="ContentBlock__Title">Hình ảnh đính kèm:</h5>
              <div className="ImageGallery">
                {complaint.images.map((imgUrl, index) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className="GalleryThumb"
                    onClick={() =>
                      setPreview({
                        isOpen: true,
                        images: complaint.images,
                        initialIndex: index,
                      })
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setPreview({
                          isOpen: true,
                          images: complaint.images,
                          initialIndex: index,
                        });
                      }
                    }}
                  >
                    <img src={imgUrl} alt={`Ảnh đính kèm ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {complaint?.responseImages && complaint.responseImages.length > 0 && (
            <div className="ContentBlock">
              <h5 className="ContentBlock__Title">Hình ảnh phản hồi:</h5>
              <div className="ImageGallery">
                {complaint.responseImages.map((imgUrl, index) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className="GalleryThumb"
                    onClick={() =>
                      setPreview({
                        isOpen: true,
                        images: complaint.responseImages,
                        initialIndex: index,
                      })
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setPreview({
                          isOpen: true,
                          images: complaint.responseImages,
                          initialIndex: index,
                        });
                      }
                    }}
                  >
                    <img src={imgUrl} alt={`Ảnh phản hồi ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {(complaint?.response || complaint?.note) && (
            <div className="TimelineBlock">
              <div className="TimelineTitle">Ghi chú xử lý / Phản hồi:</div>
              <div className="TimelineText">{complaint.response || complaint.note}</div>
            </div>
          )}
        </Card>

        {/* Modal Image Preview Popup */}
        <ModalImagePreview
          isOpen={preview.isOpen}
          images={preview.images}
          initialIndex={preview.initialIndex}
          onClose={() => setPreview((prev) => ({ ...prev, isOpen: false }))}
        />

        {/* Modal Duyệt khiếu nại */}
        <ModalResolveComplaint
          isOpen={isResolveModalOpen}
          isLoading={isLoadingResolve}
          complaintId={displayId}
          onClose={() => setIsResolveModalOpen(false)}
          onConfirm={handleConfirmResolve}
        />

        {/* Modal Từ chối khiếu nại */}
        <ModalRejectComplaint
          isOpen={isRejectModalOpen}
          isLoading={isLoadingReject}
          complaintId={displayId}
          onClose={() => setIsRejectModalOpen(false)}
          onConfirm={handleConfirmReject}
        />
      </Container>
    </div>
  );
}

export default ComplaintDetail;
